/**
 * Accueil PWA 2i Recouvrement : recherche instantanée (cache local IndexedDB),
 * scan intégré, logo + année académique, menu paramètres.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ScanLine, Loader2, WifiOff, ChevronRight, Clock, Database,
  Settings as SettingsIcon, RefreshCw, Calendar, UserCircle,
} from 'lucide-react';
import ScannerModal from '../components/ScannerModal';
import { rechercher, listRecentFiches, EtudiantResume } from '../services/verifApi';
import {
  searchLocal, needsSync, syncFromServer, countEtudiants, getLastSync,
  EtudiantCache,
} from '../services/etudiantsCache';
import { getAgent, sessionInfo } from '../services/sessionApi';
import { extractCode } from '../utils/extractCode';
import { useExitConfirm } from '../hooks/useExitConfirm';

const RECHERCHE_ONLINE_SEUIL_MS = 300; // debounce fallback online

export default function Accueil() {
  const navigate = useNavigate();
  const agent = getAgent();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EtudiantResume[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [online, setOnline] = useState<boolean>(navigator.onLine);
  const [campus, setCampus] = useState<string>('Brazzaville');
  const [annee, setAnnee] = useState<string>('');
  const [cacheCount, setCacheCount] = useState<number>(0);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const recent = useMemo(() => listRecentFiches(6), [scannerOpen, query]);

  // Interception touche back Android : double-tap pour quitter
  const exitPrompt = useExitConfirm('Appuyez à nouveau pour quitter');

  // Init : session info + statut cache + sync auto si nécessaire
  useEffect(() => {
    let alive = true;
    const boot = async () => {
      try {
        const r = await sessionInfo();
        if (!alive) return;
        if (r.institut?.campus) setCampus(r.institut.campus);
        if (r.annee_academique) setAnnee(r.annee_academique);
      } catch { /* session peut-être HS — Guard s'en occupe */ }

      setCacheCount(await countEtudiants());
      setLastSync(await getLastSync());

      // Sync auto si cache vide ou > 10 min
      if (await needsSync()) {
        setSyncing(true);
        try {
          const r = await syncFromServer();
          if (!alive) return;
          setCacheCount(r.count);
          setLastSync(r.syncedAt);
        } catch { /* pas bloquant */ } finally {
          if (alive) setSyncing(false);
        }
      }
    };
    boot();

    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);

    // Auto-sync périodique (10 min)
    const interval = setInterval(async () => {
      if (!navigator.onLine) return;
      if (await needsSync()) {
        try {
          const r = await syncFromServer();
          setCacheCount(r.count);
          setLastSync(r.syncedAt);
        } catch { /* silence */ }
      }
    }, 60 * 1000);

    return () => {
      alive = false;
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      clearInterval(interval);
    };
  }, []);

  // Recherche INSTANTANÉE via cache local + fallback online si vide
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    setError(null);
    // 1. Recherche locale (synchro, quasi-instantané)
    (async () => {
      const local = await searchLocal(q, 20);
      const mapped: EtudiantResume[] = local.map((e: EtudiantCache) => ({
        matricule: e.m,
        nom: e.n,
        prenoms: e.p,
        classe: e.c,
        niveau: e.l,
      }));
      setResults(mapped);
      // 2. Si cache vide OU 0 résultats et online → fallback API remote
      if ((mapped.length === 0 && navigator.onLine) || cacheCount === 0) {
        setLoading(true);
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const timer = setTimeout(() => {
          rechercher(q, { signal: ctrl.signal })
            .then(r => setResults(r.results || []))
            .catch((e: any) => {
              if (e.name !== 'AbortError') {
                setError(e?.message || 'Recherche impossible');
              }
            })
            .finally(() => setLoading(false));
        }, RECHERCHE_ONLINE_SEUIL_MS);
        return () => clearTimeout(timer);
      }
    })();
  }, [query, cacheCount]);

  const submitQuery = useCallback(() => {
    const ex = extractCode(query.trim());
    if (ex.matricule) {
      navigate(`/verif/fiche/${encodeURIComponent(ex.matricule)}?m=recherche_matricule`);
    } else if (results.length === 1) {
      navigate(`/verif/fiche/${encodeURIComponent(results[0].matricule)}?m=recherche_nom`);
    }
  }, [query, results, navigate]);

  const onScanned = useCallback((raw: string) => {
    setScannerOpen(false);
    const ex = extractCode(raw);
    if (ex.matricule) {
      navigate(`/verif/fiche/${encodeURIComponent(ex.matricule)}?m=scan_barcode`);
    } else if (ex.searchQuery) {
      setQuery(ex.searchQuery);
      // Auto-submit si la recherche renvoie 1 résultat unique — géré dans effect
    } else {
      setError('Code scanné non reconnu. Réessayez ou saisissez le matricule.');
    }
  }, [navigate]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await syncFromServer();
      setCacheCount(r.count);
      setLastSync(r.syncedAt);
    } catch { /* */ } finally {
      setSyncing(false);
    }
  };

  const cacheAge = lastSync ? Math.round((Date.now() - lastSync) / 60000) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header brand avec LOGO + année */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md">
        <div className="max-w-3xl mx-auto p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-md flex-shrink-0">
            <img src="/verif-icon-192.png" alt="2i" className="w-10 h-10 rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base leading-tight">2i Recouvrement</div>
            <div className="text-xs opacity-90 flex items-center gap-2 flex-wrap">
              <span>Campus {campus}</span>
              {annee && (
                <span className="inline-flex items-center gap-1">
                  <Calendar size={11} /> {annee}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/verif/settings')}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Réglages"
          >
            <SettingsIcon size={18} />
          </button>
        </div>
        {agent && (
          <div className="max-w-3xl mx-auto px-4 pb-2 flex items-center gap-2 text-xs opacity-90">
            <UserCircle size={13} /> {agent.nom_complet}
            {!online && (
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-amber-500 flex items-center gap-1">
                <WifiOff size={10} /> HORS LIGNE
              </span>
            )}
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto p-3 space-y-3">
        {/* Bouton scan XL */}
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="w-full py-6 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-2xl shadow-lg flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
        >
          <ScanLine size={30} strokeWidth={2.2} />
          <div className="text-left">
            <div className="font-bold text-lg leading-tight">Scanner</div>
            <div className="text-xs opacity-90">Carte étudiant ou QR reçu</div>
          </div>
        </button>

        {/* Recherche instantanée locale */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Recherche instantanée
            </label>
            <div className="text-[10px] text-gray-400 flex items-center gap-1">
              <Database size={10} /> {cacheCount.toLocaleString('fr-FR')} en cache
              {cacheAge !== null && (
                <span className={cacheAge > 15 ? 'text-amber-600' : 'text-emerald-600'}>
                  · il y a {cacheAge < 1 ? 'moins d\'1 min' : `${cacheAge} min`}
                </span>
              )}
              {syncing && <RefreshCw size={10} className="animate-spin ml-1" />}
            </div>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitQuery(); }}
              placeholder="Nom, prénom ou matricule…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {loading && (
              <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />
            )}
          </div>
          {error && (
            <div className="mt-2 text-xs text-red-600">{error}</div>
          )}
          {cacheCount === 0 && !syncing && (
            <button
              onClick={doSync}
              className="mt-2 text-xs text-blue-700 hover:text-blue-800 flex items-center gap-1"
            >
              <RefreshCw size={11} /> Cache vide — Synchroniser maintenant
            </button>
          )}
        </div>

        {/* Résultats */}
        {results.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
            {results.map(r => (
              <button
                key={r.matricule}
                type="button"
                onClick={() => navigate(`/verif/fiche/${encodeURIComponent(r.matricule)}?m=recherche_nom`)}
                className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-blue-50 active:bg-blue-100 transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">
                    {r.nom} {r.prenoms}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                    <span className="font-mono">{r.matricule}</span>
                    <span>·</span>
                    <span>{r.classe || r.niveau}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-400" />
              </button>
            ))}
          </div>
        )}

        {query.trim().length >= 2 && !loading && results.length === 0 && !error && (
          <div className="text-center text-sm text-gray-500 py-6">
            Aucun étudiant trouvé.
          </div>
        )}

        {/* Historique récent */}
        {query.trim().length < 2 && recent.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase">
              <Clock size={12} /> Consultés récemment
            </div>
            <div className="divide-y divide-gray-100">
              {recent.map(r => (
                <button
                  key={r.matricule}
                  type="button"
                  onClick={() => navigate(`/verif/fiche/${encodeURIComponent(r.matricule)}`)}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 truncate">{r.nom} {r.prenoms}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{r.matricule}</div>
                  </div>
                  <ChevronRight size={14} className="text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-[11px] text-gray-400 pt-4 pb-8">
          Institut International 2i · Usage restreint aux agents de recouvrement
        </div>
      </div>

      <ScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={onScanned}
      />

      {/* Toast "Appuyez à nouveau pour quitter" */}
      {exitPrompt && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2.5 rounded-full shadow-lg text-sm font-medium animate-pulse">
          {exitPrompt}
        </div>
      )}
    </div>
  );
}
